import { supabaseClient } from './supabase';
import { logger } from './logger';
import type { EventItem, Place } from '../types/shared/event';
import { HOTEL_ID } from './supabase/configClient';

interface RpcEventRow {
  id: string;
  occurrence_id: string;
  show_id: string;
  sectiontype: string;
  name: string;
  description: string | null;
  category: string | null;
  type: string | null;
  place: string;
  place_id: string;
  place_description: string | null;
  telephone: string | null;
  url: string | null;
  booking: string | null;
  distance: number | null;
  timewalk: number | null;
  timedrive: number | null;
  timebike: number | null;
  date: string;
  hour: string | null;
  end_time: string | null;
  duration: number | null;
  fulladress: string | null;
  latitude: number | null;
  longitude: number | null;
  price: string | null;
  offer: string | null;
  image: string | null;
  image_metadata: { url: string; size: number; type: string } | null;
}

class EventClient {
  private static instance: EventClient;

  private constructor() {}

  public static getInstance(): EventClient {
    if (!EventClient.instance) {
      EventClient.instance = new EventClient();
    }
    return EventClient.instance;
  }

  public async getEvents(sectionId: string, startDate?: string, endDate?: string): Promise<EventItem[]> {
    try {
      if (!sectionId) {
        throw new Error('Section ID is required');
      }

      const { data: rpcData, error: rpcError } = await supabaseClient.rpc('get_event_section_data', {
        p_section_id: sectionId,
        p_hotel_id: HOTEL_ID,
        p_date_start: startDate ?? null,
        p_date_end: endDate ?? null
      });

      if (rpcError) throw rpcError;

      const rawData = (rpcData as any[]) || [];
      const rows: RpcEventRow[] = rawData.map(row =>
        row.get_event_section_data ?? row
      );

      const [descriptionsResult, offersResult] = await Promise.all([
        supabaseClient
          .from('translations_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId)
          .eq('content_type', 'description'),

        supabaseClient
          .from('translations_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId)
          .eq('content_type', 'offer')
      ]);

      const langs = ['fr', 'en', 'es', 'it', 'pt', 'de', 'nl', 'ja', 'zh', 'ru'];

      const descriptionsMap = new Map<string, Record<string, string>>();
      if (descriptionsResult.data) {
        for (const t of descriptionsResult.data) {
          const translations: Record<string, string> = {};
          for (const lang of langs) {
            if ((t as any)[lang]) translations[lang] = (t as any)[lang];
          }
          if ((t as any).item_id) descriptionsMap.set((t as any).item_id, translations);
          if ((t as any).idshow) descriptionsMap.set((t as any).idshow, translations);
        }
      }

      const offersMap = new Map<string, Record<string, string>>();
      if (offersResult.data) {
        for (const t of offersResult.data) {
          const translations: Record<string, string> = {};
          for (const lang of langs) {
            if ((t as any)[lang]) translations[lang] = (t as any)[lang];
          }
          if ((t as any).item_id) offersMap.set((t as any).item_id, translations);
          if ((t as any).idshow) offersMap.set((t as any).idshow, translations);
        }
      }

      const events: EventItem[] = rows.map(row => {
        const descriptions = descriptionsMap.get(row.occurrence_id) || descriptionsMap.get(row.show_id) || {};
        const offers = offersMap.get(row.occurrence_id) || offersMap.get(row.show_id) || {};

        return {
          id: row.id,
          name: row.name,
          descriptions,
          description: row.description || '',
          offers,
          offer: row.offer || '',
          fulladress: row.fulladress || '',
          telephone: row.telephone || '',
          url: row.url || '',
          category: row.category || 'Autre',
          type: row.type || '',
          image: row.image_metadata?.url || row.image || '',
          latitude: row.latitude || 0,
          longitude: row.longitude || 0,
          distance: row.distance || 0,
          timewalk: row.timewalk?.toString() || '',
          timedrive: row.timedrive?.toString() || '',
          timebike: row.timebike?.toString() || '',
          booking: row.booking || '',
          price: row.price || '',
          artiste: '',
          place: row.place,
          place_id: row.place_id,
          date: typeof row.date === 'string' ? row.date.substring(0, 10) : String(row.date),
          hour: row.hour ? row.hour.substring(0, 5) : '00:00',
          idShow: row.show_id,
          idshow: row.show_id,
          show_id: row.show_id,
          google_map: '',
          source: 'event'
        } as EventItem;
      });

      return events;

    } catch (error) {
      logger.error('Error fetching events', {
        category: 'API',
        data: { error, sectionId, hotelId: HOTEL_ID }
      });
      throw error;
    }
  }

  public getPlaces(events: EventItem[]): Place[] {
    try {
      const placeMap = new Map<string, Place>();

      events.forEach(event => {
        if (!placeMap.has(event.place)) {
          placeMap.set(event.place, {
            name: event.place,
            latitude: event.latitude,
            longitude: event.longitude,
            distance: event.distance,
            fulladress: event.fulladress,
            itinerary: event.url,
            shows: []
          });
        }

        const place = placeMap.get(event.place)!;
        const showId = (event as any).idShow || (event as any).show_id || '';
        let show = place.shows.find(s => (s as any).idShow === showId);

        if (!show) {
          show = {
            id: event.id,
            idShow: showId,
            name: event.name,
            image: event.image,
            type: event.type,
            source: 'event',
            events: []
          } as any;
          place.shows.push(show);
        }

        (show as any).events.push(event);
      });

      const places = Array.from(placeMap.values()).map(place => ({
        ...place,
        shows: place.shows.sort((a, b) => {
          const aEvent = (a as any).events[0];
          const bEvent = (b as any).events[0];
          const dateCompare = aEvent.date.localeCompare(bEvent.date);
          return dateCompare !== 0 ? dateCompare : aEvent.hour.localeCompare(bEvent.hour);
        })
      }));

      return places;

    } catch (error) {
      logger.error('Error creating places', {
        category: 'API',
        data: { error }
      });
      return [];
    }
  }
}

const eventClient = EventClient.getInstance();

export { eventClient };
